export type SceneViewPreset = "top" | "front" | "back" | "left" | "right" | "iso"

export type Vec3 = [number, number, number]

/** Fly camera pitch limit (rad). Straight down gimbal-locks the WASD basis. */
export const VIEW_ORBIT_PITCH_MAX = 1.45

/** Orbit-drag sensitivity, rad per CSS pixel. Scene look stays slower. */
export const VIEW_CUBE_ORBIT_RAD_PER_PX = 0.014

/** Below this dot product a free camera is not "on" a preset. */
export const VIEW_MATCH_DOT = Math.cos(0.2)

const VIEW_IDS: readonly SceneViewPreset[] = ["top", "front", "back", "left", "right", "iso"]

function unit(x: number, y: number, z: number): Vec3 {
  const length = Math.hypot(x, y, z) || 1
  return [n0(x / length), n0(y / length), n0(z / length)]
}

/** `-0` breaks deep equality and can flip an atan2 quadrant. */
function n0(value: number): number {
  return value === 0 ? 0 : value
}

/**
 * Widget Z-up `(x, y, z)` → scene Y-up `(x, z, -y)`.
 * Same map as the view-cube prototype (`toInternal`).
 */
export function widgetToScene(v: Vec3): Vec3 {
  return [n0(v[0]), n0(v[2]), n0(-v[1])]
}

/** Scene Y-up `(x, y, z)` → widget Z-up `(x, -z, y)`. */
export function sceneToWidget(v: Vec3): Vec3 {
  return [n0(v[0]), n0(-v[2]), n0(v[1])]
}

/**
 * Direction from the focus point to the camera, scene Y-up.
 * `top` is exact +Y. Free flight still clamps pitch; the pole is held as a lookAt pose.
 * Other views are the spec axes, without the old `0.18` side tilt.
 */
export const VIEW_PRESET_DIRS: Record<SceneViewPreset, Vec3> = {
  top: [0, 1, 0],
  front: widgetToScene([0, -1, 0]),
  back: widgetToScene([0, 1, 0]),
  left: widgetToScene([-1, 0, 0]),
  right: widgetToScene([1, 0, 0]),
  iso: widgetToScene(unit(-1, -1, 1)),
}

/** Pitch of a camera that sits on `dirFromFocus` and looks back at the focus (YXZ euler). */
export function pitchForFocusDirection(dirFromFocus: Vec3): number {
  const length = Math.hypot(dirFromFocus[0], dirFromFocus[1], dirFromFocus[2]) || 1
  const y = Math.min(1, Math.max(-1, dirFromFocus[1] / length))
  return -Math.asin(y)
}

export function matchViewPreset(dirFromFocus: Vec3): SceneViewPreset | null {
  const length = Math.hypot(dirFromFocus[0], dirFromFocus[1], dirFromFocus[2]) || 1
  let best: SceneViewPreset | null = null
  let bestDot = VIEW_MATCH_DOT
  for (const id of VIEW_IDS) {
    const dir = VIEW_PRESET_DIRS[id]
    const dot = (dirFromFocus[0] * dir[0] + dirFromFocus[1] * dir[1] + dirFromFocus[2] * dir[2]) / length
    if (dot > bestDot) {
      bestDot = dot
      best = id
    }
  }
  return best
}
